/**
 * Pure helpers for the V3 Credin confirmation screen (`GenerateCredinV3Page`).
 */
import { getIncomingDebinAccountName } from "util/accounts";
import { getAccountType } from "util/cbuCmf";
import { isValidCVU } from "util/credin";
import * as i18n from "util/i18n";

import { formatCredinLimitAmount, getCredinCurrencyLabel } from "v3/pages/transfers/createCredin";

/**
 * Maps seller + account into the UI-kit blue summary box.
 *
 * @param {object|undefined|null} seller
 * @param {Array<object>} accounts
 * @returns {{
 *   debitAccountLabel: string,
 *   availableLabel?: string,
 *   concept: string,
 *   description: string,
 *   expiration: string
 * }}
 */
export function mapCredinConfirmationSellerSummary(seller, accounts) {
    if (!seller) {
        return {
            debitAccountLabel: "-",
            concept: "-",
            description: "-",
            expiration: "-",
        };
    }

    const selectedAccount = Array.isArray(accounts)
        ? accounts.find((account) => account.idProduct === seller.account)
        : undefined;

    const debitAccountLabel = selectedAccount ? getIncomingDebinAccountName(selectedAccount) : "-";
    const availableLabel = selectedAccount
        ? `${i18n.get("credin.confirmation.available")}: ${formatCredinLimitAmount(
              selectedAccount.balance,
              selectedAccount.currency,
          )}`
        : undefined;

    const concept = seller.concept ? i18n.get(`transfers.concept.${seller.concept}`, seller.concept) : "-";

    return {
        debitAccountLabel,
        availableLabel,
        concept,
        description: seller.description || "-",
        expiration: "-",
    };
}

/**
 * Maps a preview recipient (`debin.recipients` item) into a UI-kit card row.
 *
 * @param {object} recipient
 * @returns {object}
 */
export function mapCredinConfirmationRecipient(recipient) {
    const {
        id,
        recipientName,
        recipientDocumentType,
        recipientDocumentNumber,
        amount,
        accountNumber,
        cbu,
        localBank,
        reference,
    } = recipient || {};

    const currencyCode = amount && amount.currency;
    const accountType = cbu ? getAccountType(cbu) : null;
    const bankLabelKey = accountType === "cvu" ? "entityName" : "bankName";

    const documentTypeLabel = recipientDocumentType
        ? i18n.get(`documentType.label.${String(recipientDocumentType).toUpperCase()}`, recipientDocumentType)
        : i18n.get("credin.confirmation.documentType");

    return {
        id: id || "-",
        owner: recipientName || "-",
        documentLabel: `${documentTypeLabel} ${recipientDocumentNumber || ""}`.trim(),
        accountNumber: accountNumber || "-",
        cbu: cbu || "-",
        bankName: localBank || "-",
        bankLabel: i18n.get(`credin.confirmation.${bankLabelKey}`),
        bankLabelKey,
        reference: reference || "-",
        amount: amount && amount.quantity != null ? amount.quantity : 0,
        currency: getCredinCurrencyLabel(currencyCode),
    };
}

/**
 * Maps the preview recipients array for the UI-kit confirmation page.
 *
 * @param {Array<object>|undefined|null} recipients
 * @returns {Array<object>}
 */
export function mapCredinConfirmationRecipients(recipients) {
    if (!Array.isArray(recipients)) {
        return [];
    }
    return recipients.map(mapCredinConfirmationRecipient);
}

/**
 * Aggregates preview recipients into total / CBU / CVU totals for the confirmation totalizer.
 *
 * @param {Array<object>|undefined|null} recipients
 * @returns {{
 *   totalCount: number,
 *   totalAmount: number,
 *   cbuCount: number,
 *   cbuAmount: number,
 *   cvuCount: number,
 *   cvuAmount: number,
 *   currency: string
 * }}
 */
export function mapCredinConfirmationTotals(recipients) {
    const emptyTotals = {
        totalCount: 0,
        totalAmount: 0,
        cbuCount: 0,
        cbuAmount: 0,
        cvuCount: 0,
        cvuAmount: 0,
        currency: "ARS",
    };

    if (!Array.isArray(recipients)) {
        return emptyTotals;
    }

    return recipients.reduce(
        (accumulator, recipient) => {
            const quantity = recipient?.amount?.quantity;
            const parsedAmount = Number.isFinite(quantity) ? quantity : Number.parseFloat(quantity) || 0;
            const currencyCode = recipient?.amount?.currency;
            const isCvu = isValidCVU(recipient?.cbu);

            return {
                totalCount: accumulator.totalCount + 1,
                totalAmount: accumulator.totalAmount + parsedAmount,
                cbuCount: accumulator.cbuCount + (isCvu ? 0 : 1),
                cbuAmount: accumulator.cbuAmount + (isCvu ? 0 : parsedAmount),
                cvuCount: accumulator.cvuCount + (isCvu ? 1 : 0),
                cvuAmount: accumulator.cvuAmount + (isCvu ? parsedAmount : 0),
                currency: currencyCode ? getCredinCurrencyLabel(currencyCode) : accumulator.currency,
            };
        },
        { ...emptyTotals },
    );
}
